import type { PromptTemplateId } from "@pluck/api/lib/board/compose";
import type { AppRouter } from "@pluck/api/routers/index";
import type { inferRouterOutputs } from "@trpc/server";
import { createContext, useContext } from "react";
import type { PluckSummary } from "@/components/pluck-card";
import type { AspectRatio, PromptMode, QualityTier } from "@/lib/board-graph";
import type { HostedModelInfo } from "@/lib/hosted-models";

export type GenerationRow =
	inferRouterOutputs<AppRouter>["generations"]["listForBoard"][number];

export type UploadRow =
	inferRouterOutputs<AppRouter>["uploads"]["list"][number];

export interface BoardData {
	boardId: string;
	canGenerate: boolean;
	/** Hosted credits left (null while loading or signed out). */
	creditBalance: number | null;
	generationsById: Map<string, GenerationRow>;
	/** Models runnable on Pluck's servers (empty when unavailable). */
	hostedModels: HostedModelInfo[];
	/** Running in Pluck Desktop (local Codex available). */
	isDesktop: boolean;
	/** Permanently deletes an uploaded image (from every board). */
	onDeleteUpload: (uploadId: string) => void;
	onGenerate: (promptNodeId: string) => void;
	onRemoveNode: (nodeId: string) => void;
	onSetPromptMode: (nodeId: string, mode: PromptMode) => void;
	onSetPromptModel: (nodeId: string, model: string) => void;
	onSetPromptSettings: (
		nodeId: string,
		settings: { aspect?: AspectRatio; quality?: QualityTier }
	) => void;
	onUpdatePromptText: (nodeId: string, text: string) => void;
	plucksById: Map<string, PluckSummary>;
	/** Generation cards wired into each prompt, by prompt node id. */
	promptGenerationInputs: Map<string, number>;
	/** Runs and reference images per run for each prompt (for cost previews). */
	promptRunShapes: Map<string, { runs: number; referencesPerRun: number }>;
	/** The account's prompt template (decides the default aspect ratio). */
	promptTemplate: PromptTemplateId;
	runningPromptNodeIds: Set<string>;
	uploadsById: Map<string, UploadRow>;
}

const BoardContext = createContext<BoardData | null>(null);

export const BoardDataProvider = BoardContext.Provider;

export function useBoardData(): BoardData {
	const ctx = useContext(BoardContext);
	if (!ctx) {
		throw new Error("useBoardData must be used inside a board");
	}
	return ctx;
}
